import React from "react";
import { Link } from "react-router-dom";
import { cta, CTA_STYLES, CTA_COLORS } from "@/lib/cta";
import { BRAND } from "@/lib/brand";
import heroBackground from "@/assets/hero-background.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { Gem, Eye, BadgeDollarSign, Wrench, TrendingUp, UserCheck, ClipboardCheck, Rocket, Handshake, ShieldCheck, ChevronDown, ChevronUp, Award, FileCheck, Users, DollarSign, Building, Briefcase } from "lucide-react";
import { useState } from "react";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

const THEME = BRAND;



const conceptCards = [
  {
    icon: Gem,
    title: "Expertise for Equity",
    description: "Stop selling just your time. Exchange your licenses, skill cards, or proven know-how for direct equity participation in the companies you help acquire.",
  },
  {
    icon: Eye,
    title: "Multilateral Visibility",
    description: "Be instantly discovered by a network of sellers, buyers, and brokers thanks to our intelligent search engine.",
  },
  {
    icon: BadgeDollarSign,
    title: "Commission-Free Model",
    description: "A simple annual subscription. No success fees, you keep 100% of your revenue and remain in control of your pricing negotiations.",
  },
];

const strategicAdvantages = [
  {
    icon: FileCheck,
    title: "Qualified Deal Flow",
    titleFr: "Flux de transactions qualifiées",
    description: "Access active opportunities where your involvement is crucial to the project's success.",
    descriptionFr: "Accédez à des opportunités actives où votre implication est cruciale au succès du projet.",
  },
  {
    icon: Users,
    title: "Integrated Collaboration",
    titleFr: "Collaboration intégrée",
    description: "Participate in strategic meetings with sellers, lenders, and lawyers within a standardized workflow.",
    descriptionFr: "Participez à des réunions stratégiques avec vendeurs, prêteurs et avocats dans un flux de travail standardisé.",
  },
  {
    icon: DollarSign,
    title: "Complete Independence",
    titleFr: "Indépendance complète",
    description: "Direct invoicing and complete contractual autonomy with your clients.",
    descriptionFr: "Facturation directe et autonomie contractuelle complète avec vos clients.",
  },
];

const whoCanBecomeConsultant = [
  {
    icon: Wrench,
    title: "Skilled Tradespeople",
    titleFr: "Professionnels qualifiés",
    description: "Holders of specialized licenses, skill cards, or specific permits. Examples: electricians, plumbers, HVAC technicians, mechanics, welders, and other licensed trades professionals.",
    descriptionFr: "Détenteurs de licences spécialisées, cartes de compétence ou permis spécifiques. Exemples : électriciens, plombiers, techniciens CVC, mécaniciens, soudeurs et autres professionnels des métiers licenciés.",
    examples: ["Trade licenses", "Skill cards", "Industry certifications", "Professional permits"],
    examplesFr: ["Licences de métier", "Cartes de compétence", "Certifications industrielles", "Permis professionnels"],
  },
  {
    icon: TrendingUp,
    title: "Value Accelerators",
    titleFr: "Accélérateurs de valeur",
    description: "Operational specialists capable of optimizing revenue and post-acquisition valuation. Includes operations managers, sales experts, marketing specialists, and efficiency consultants.",
    descriptionFr: "Spécialistes opérationnels capables d'optimiser les revenus et la valorisation post-acquisition. Comprend les directeurs des opérations, experts en ventes, spécialistes marketing et consultants en efficacité.",
    examples: ["Operations optimization", "Revenue growth", "Cost reduction", "Process improvement"],
    examplesFr: ["Optimisation des opérations", "Croissance des revenus", "Réduction des coûts", "Amélioration des processus"],
  },
  {
    icon: Briefcase,
    title: "Industry Experts",
    titleFr: "Experts sectoriels",
    description: "Professionals with deep knowledge in specific industries such as hospitality, manufacturing, retail, healthcare, or technology. Your expertise helps buyers navigate sector-specific challenges.",
    descriptionFr: "Professionnels avec une connaissance approfondie dans des secteurs spécifiques comme l'hôtellerie, la fabrication, le commerce de détail, la santé ou la technologie. Votre expertise aide les acheteurs à naviguer les défis spécifiques au secteur.",
    examples: ["Sector analysis", "Market insights", "Regulatory knowledge", "Industry networks"],
    examplesFr: ["Analyse sectorielle", "Perspectives de marché", "Connaissance réglementaire", "Réseaux industriels"],
  },
  {
    icon: Users,
    title: "Business Advisors",
    titleFr: "Conseillers d'affaires",
    description: "Former business owners, executives, or consultants with proven track records in business development, turnaround management, or strategic planning.",
    descriptionFr: "Anciens propriétaires d'entreprise, cadres ou consultants avec des antécédents prouvés en développement commercial, gestion de redressement ou planification stratégique.",
    examples: ["Business development", "Strategic planning", "Turnaround expertise", "Leadership coaching"],
    examplesFr: ["Développement commercial", "Planification stratégique", "Expertise en redressement", "Coaching de leadership"],
  },
];

const howItWorksSteps = [
  {
    icon: ClipboardCheck,
    step: 1,
    title: "Registration & Subscription",
    titleFr: "Inscription et abonnement",
    description: "Simplified annual membership with clear pricing. No hidden fees, no commission on your deals.",
    descriptionFr: "Adhésion annuelle simplifiée avec tarification claire. Pas de frais cachés, pas de commission sur vos transactions.",
  },
  {
    icon: ShieldCheck,
    step: 2,
    title: "Verification (Background Check)",
    titleFr: "Vérification (Background Check)",
    description: "Profile validation to ensure network quality. This process guarantees trust and security for all parties.",
    descriptionFr: "Validation du profil pour assurer la qualité du réseau. Ce processus garantit confiance et sécurité pour toutes les parties.",
    badge: true,
  },
  {
    icon: Rocket,
    step: 3,
    title: "Activation & Listing",
    titleFr: "Activation et référencement",
    description: "Immediate integration into the public directory. Your profile becomes visible to buyers, sellers, and brokers.",
    descriptionFr: "Intégration immédiate dans l'annuaire public. Votre profil devient visible pour les acheteurs, vendeurs et courtiers.",
  },
  {
    icon: Handshake,
    step: 4,
    title: "Collaboration",
    titleFr: "Collaboration",
    description: "User selection, meetings, and deal closing. You choose your clients and negotiate your own terms.",
    descriptionFr: "Sélection des utilisateurs, réunions et clôture des transactions. Vous choisissez vos clients et négociez vos propres conditions.",
  },
];

const consultantFAQs = [
  {
    question: "How much does the Consultant subscription cost?",
    questionFr: "Combien coûte l'abonnement Consultant ?",
    answer: "The annual subscription is $504 USD (launch price at 30% discount from regular $720/year rate). This is a one-time annual payment, not a recurring monthly charge.",
    answerFr: "L'abonnement annuel est de 504 $ USD (prix de lancement avec 30% de réduction sur le tarif régulier de 720 $/an). C'est un paiement annuel unique, pas un prélèvement mensuel récurrent.",
  },
  {
    question: "What does the subscription include?",
    questionFr: "Que comprend l'abonnement ?",
    answer: "Your subscription includes: directory listing, Deal Monitor access, secure document vault, integrated calendar, messaging system, and community participation.",
    answerFr: "Votre abonnement comprend : référencement dans l'annuaire, accès au Deal Monitor, coffre-fort de documents sécurisé, calendrier intégré, système de messagerie et participation à la communauté.",
  },
  {
    question: "Are there any commissions on my deals?",
    questionFr: "Y a-t-il des commissions sur mes transactions ?",
    answer: "No. You keep 100% of your revenue. AcquizGroup operates on a subscription model only—we never take a cut of your earnings or success fees.",
    answerFr: "Non. Vous conservez 100% de vos revenus. AcquizGroup fonctionne uniquement sur un modèle d'abonnement—nous ne prenons jamais de part sur vos gains ou frais de succès.",
  },
  {
    question: "What is the background check process?",
    questionFr: "Quel est le processus de vérification des antécédents ?",
    answer: "We verify professional credentials, certifications, and conduct standard background screening to ensure network quality. This typically takes 3-5 business days.",
    answerFr: "Nous vérifions les qualifications professionnelles, les certifications et effectuons une vérification standard des antécédents pour assurer la qualité du réseau. Cela prend généralement 3 à 5 jours ouvrables.",
  },
  {
    question: "Can I negotiate equity participation instead of fees?",
    questionFr: "Puis-je négocier une participation au capital au lieu d'honoraires ?",
    answer: "Absolutely! Our 'Expertise for Equity' model allows you to negotiate equity stakes in acquisitions where you provide significant value. This is entirely between you and the buyer.",
    answerFr: "Absolument ! Notre modèle 'Expertise contre Équité' vous permet de négocier des participations dans les acquisitions où vous apportez une valeur significative. C'est entièrement entre vous et l'acheteur.",
  },
  {
    question: "What is my operational role as a Consultant?",
    questionFr: "Quel est mon rôle opérationnel en tant que Consultant ?",
    answer: "You provide expertise, advice, and support to buyers during the acquisition process. This can include due diligence support, operational assessments, transition planning, or post-acquisition optimization—depending on your specialty.",
    answerFr: "Vous fournissez expertise, conseils et soutien aux acheteurs pendant le processus d'acquisition. Cela peut inclure le soutien à la diligence raisonnable, les évaluations opérationnelles, la planification de transition ou l'optimisation post-acquisition—selon votre spécialité.",
  },
];

export default function ConsultantsPage() {
  const { language, setLanguage } = useLanguage();
  const ctaTexts = cta('consultant', language);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <SiteHeader lang={language} onToggleLang={() => setLanguage(language === 'fr' ? 'en' : 'fr')} />

      {/* Hero */}
      <section className="w-full relative overflow-hidden" style={{ background: `linear-gradient(180deg, ${THEME.navy}, ${THEME.navyDark})` }}>
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${THEME.navy}CC, ${THEME.navyDark}CC)` }}></div>
        <div className="relative mx-auto max-w-7xl px-4 py-20 text-center">
          <p className="text-sm uppercase tracking-wide" style={{ color: THEME.silver }}>CONSULTANTS</p>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold text-white">
            {language === "fr" ? "Transformez votre expertise en capital" : "Transform your expertise into capital"}
          </h1>
          <p className="mt-6 text-lg max-w-3xl mx-auto" style={{ color: THEME.silver }}>
            {language === "fr" 
              ? "Rejoignez l'écosystème AcquizGroup pour accéder à des transactions qualifiées. Monétisez votre savoir-faire via des honoraires ou devenez partenaire stratégique en échangeant votre expertise contre des participations."
              : "Join the AcquizGroup ecosystem to access qualified transactions. Monetize your know-how through fees or become a strategic partner by exchanging your expertise for equity."
            }
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register/consultant" className={CTA_STYLES.primary} style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}>
              {ctaTexts.primary}
            </Link>
            <Link to="/contact" aria-label={ctaTexts.aria} className={`${CTA_STYLES.secondary} border-white/30 text-white`}>
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Spacing after hero */}
      <div className="py-8"></div>

      {/* The Concept */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: THEME.navy }}>
          {language === "fr" ? "Avantages stratégiques" : "Strategic Advantages"}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {conceptCards.map((card, i) => (
            <div key={i} className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-6 shadow-sm">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: THEME.navy }}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-lg" style={{ color: THEME.navy }}>{card.title}</h3>
              <p className="mt-2 text-sm" style={{ color: THEME.graphite }}>{card.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Strategic Advantages */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: THEME.navy }}>
          {language === "fr" ? "Le Concept" : "The Concept"}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {strategicAdvantages.map((item, i) => (
            <div key={i} className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-6 shadow-sm">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: THEME.navy }}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-lg" style={{ color: THEME.navy }}>
                {language === "fr" ? item.titleFr : item.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: THEME.graphite }}>
                {language === "fr" ? item.descriptionFr : item.description}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link 
            to="/general-landing" 
            className="inline-block rounded-lg px-6 py-3 font-semibold text-sm transition-all hover:opacity-90"
            style={{ background: `linear-gradient(90deg, ${THEME.goldStart}, ${THEME.goldEnd})`, color: THEME.navy }}
          >
            {language === "fr" ? "En savoir plus" : "Learn more"}
          </Link>
        </div>
      </section>

      {/* Who can become a Consultant */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: THEME.navy }}>
          {language === "fr" ? "Qui peut devenir Consultant ?" : "Who can become a Consultant?"}
        </h2>
        <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: THEME.graphite }}>
          {language === "fr" 
            ? "Notre réseau accueille des professionnels qualifiés de divers horizons"
            : "Our network welcomes qualified professionals from diverse backgrounds"
          }
        </p>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {whoCanBecomeConsultant.map((item, i) => (
            <div key={i} className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: THEME.navy }}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg" style={{ color: THEME.navy }}>
                    {language === "fr" ? item.titleFr : item.title}
                  </h3>
                </div>
              </div>
              <p className="text-sm mb-4" style={{ color: THEME.graphite }}>
                {language === "fr" ? item.descriptionFr : item.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {(language === "fr" ? item.examplesFr : item.examples).map((example, j) => (
                  <span 
                    key={j} 
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${THEME.navy}10`, color: THEME.navy }}
                  >
                    {example}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How it Works - Visual Stepper */}
      <section className="mx-auto max-w-7xl px-4 py-16 bg-gradient-to-b from-white to-slate-50">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: THEME.navy }}>
          {language === "fr" ? "Comment ça fonctionne" : "How it Works"}
        </h2>
        <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: THEME.graphite }}>
          {language === "fr" 
            ? "Un processus simple en 4 étapes pour rejoindre notre réseau de consultants vérifiés"
            : "A simple 4-step process to join our network of verified consultants"
          }
        </p>
        <div className="grid md:grid-cols-4 gap-6 relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-20 left-[12.5%] right-[12.5%] h-1 bg-gradient-to-r from-[#CEAB65] via-[#1D314D] to-[#CEAB65] rounded-full opacity-30"></div>
          
          {howItWorksSteps.map((step, i) => (
            <div key={i} className="relative flex flex-col items-center text-center">
              <div className="relative">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white relative z-10"
                  style={{ backgroundColor: THEME.navy }}
                >
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <div 
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-20"
                  style={{ background: `linear-gradient(90deg, ${THEME.goldStart}, ${THEME.goldEnd})`, color: THEME.navy }}
                >
                  {step.step}
                </div>
                {step.badge && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500 text-white shadow-md whitespace-nowrap">
                    <ShieldCheck className="w-3 h-3" />
                    {language === "fr" ? "Expert Vérifié" : "Verified Expert"}
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-base mt-2" style={{ color: THEME.navy }}>
                {language === "fr" ? step.titleFr : step.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: THEME.graphite }}>
                {language === "fr" ? step.descriptionFr : step.description}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link 
            to="/general-landing" 
            className="inline-block rounded-lg px-6 py-3 font-semibold text-sm transition-all hover:opacity-90"
            style={{ background: `linear-gradient(90deg, ${THEME.goldStart}, ${THEME.goldEnd})`, color: THEME.navy }}
          >
            {language === "fr" ? "En savoir plus" : "Learn more"}
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: THEME.navy }}>
          {language === "fr" ? "Questions fréquentes" : "Frequently Asked Questions"}
        </h2>
        <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: THEME.graphite }}>
          {language === "fr" 
            ? "Tout ce que vous devez savoir sur le programme Consultant"
            : "Everything you need to know about the Consultant program"
          }
        </p>
        <div className="space-y-3">
          {consultantFAQs.map((faq, i) => (
            <FAQItem 
              key={i} 
              question={language === "fr" ? faq.questionFr : faq.question}
              answer={language === "fr" ? faq.answerFr : faq.answer}
            />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16">
        <div className="rounded-2xl p-8 text-center shadow-lg" style={{ background: `linear-gradient(180deg, ${THEME.navyDark}, ${THEME.navy})` }}>
          <h2 className="text-2xl font-semibold text-white">
            {language === "fr" ? "Prêt à amplifier votre impact ?" : "Ready to amplify your impact?"}
          </h2>
          <p className="mt-2 text-sm" style={{ color: THEME.silver }}>
            {language === "fr" 
              ? "Inscrivez-vous, soyez découvert et collaborez sur des acquisitions qualifiées."
              : "Get listed, be discovered, and collaborate on qualified acquisitions."
            }
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Link to="/register/consultant" className={CTA_STYLES.primary} style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}>
              {ctaTexts.primary}
            </Link>
            <Link 
              to="/consultant/payment" 
              className="inline-block rounded-lg px-8 py-3 text-lg font-semibold font-sans"
              style={{ background: '#CEAB65', color: '#1D314D', border: '2px solid #CEAB65' }}
            >
              {language === 'fr' ? 'Voir les forfaits' : 'View plans'}
            </Link>
            <Link to="/contact" aria-label={ctaTexts.aria} className={`${CTA_STYLES.secondary} border-white/30 text-white`}>
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter lang={language} />
      <ScrollToTop />
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium" style={{ color: BRAND.navy }}>{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: BRAND.goldEnd }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: BRAND.goldEnd }} />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4 text-sm" style={{ color: BRAND.graphite }}>
          {answer}
        </div>
      )}
    </div>
  );
}
